import React, { useState } from 'react';
import useMetaTags from "../hooks/useMetaTags"; // Adjust path based on your project structure
import { 
  MessageCircle, 
  MapPin, 
  Phone, 
  Mail, 
  Send,
  Clock,
  CheckCircle
} from 'lucide-react';

const ContactUs = () => {
  useMetaTags({
    title: "Contact Us | Organic Pads - Get in Touch for Eco-Friendly Period Care",
    description: "Have questions about our organic sanitary pads? Contact us for customer support, inquiries, or partnership opportunities. We're here to help!",
    keywords: "contact organic pads, customer support, organic sanitary pads help, eco-friendly period products support, sustainable menstrual care contact",
    author: "Organic Pads",
    robots: "index, follow",
    canonical: "/contact",
    ogTitle: "Contact Us | Organic Pads - Get in Touch for Eco-Friendly Period Care",
    ogDescription: "Reach out to us for support, inquiries, or collaborations regarding our organic and chemical-free sanitary pads. We're happy to assist you!",
    ogImage: "https://organicpads.shop/logo.png",
    ogUrl: "https://organicpads.shop/contact-us",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Contact Us | Organic Pads - Get in Touch for Eco-Friendly Period Care",
    twitterDescription: "Looking for support or have inquiries about our organic sanitary pads? Contact us today!",
    twitterImage: "https://organicpads.shop/logo.png",
    twitterSite: "@OrganicPads",
    twitterCreator: "@OrganicPads",
  });

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Organic Pads Contact",
    "url": "https://organicpads.shop/contact-us",
    "description": "Get in touch with Organic Pads for customer inquiries, orders, or partnership opportunities related to eco-friendly menstrual products.",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+917203802493",
      "contactType": "customer service",
      "email": "organicpads.shop@gmail.com",
      "availableLanguage": ["English", "Hindi"]
    },
    "sameAs": [
      "https://www.facebook.com/profile.php?id=61573042873807",
      "https://www.instagram.com/organicpads_/",
      "https://www.linkedin.com/company/organic-pads",
      "https://www.youtube.com/channel/UCG5-R0zTI-2DAM14ZC0ZD-A",
      "https://x.com/OrganicPads"
    ]
  }
  

  const [formStatus, setFormStatus] = useState('idle');

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('sending');
    // Simulate form submission
    setTimeout(() => setFormStatus('success'), 1500);
  };

  return (
    <>
    <div className="max-w-7xl mx-auto px-4 mt-10 py-16">
      {/* Header Section */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 text-[#679729] mb-4">
          <MessageCircle size={24} />
          <span className="text-lg font-medium">Get in Touch</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
          We'd Love to Hear From You
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Have questions about our organic products or sustainability initiatives? 
          We're here to help you make the switch to a healthier, more eco-conscious lifestyle.
        </p>
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        {/* Contact Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 relative overflow-hidden">
          {/* Decorative Element */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#679729]/10 rounded-bl-full -z-10" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#679729]/5 rounded-tr-full -z-10" />
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input 
                  type="text"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#679729] focus:ring-2 focus:ring-[#679729]/20 outline-none transition-all"
                  placeholder="Jane"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input 
                  type="text"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#679729] focus:ring-2 focus:ring-[#679729]/20 outline-none transition-all"
                  placeholder="Doe"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input 
                type="email"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#679729] focus:ring-2 focus:ring-[#679729]/20 outline-none transition-all"
                placeholder="jane@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea 
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#679729] focus:ring-2 focus:ring-[#679729]/20 outline-none transition-all h-32"
                placeholder="How can we help you?"
              />
            </div>
            <button 
              type="submit"
              disabled={formStatus !== 'idle'}
              className="w-full bg-[#679729] text-white py-4 rounded-lg hover:bg-[#679729]/90 transition-colors duration-300 flex items-center justify-center gap-2"
            >
              {formStatus === 'idle' && (
                <>
                  <Send size={20} />
                  Send Message
                </>
              )}
              {formStatus === 'sending' && (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {formStatus === 'success' && (
                <>
                  <CheckCircle size={20} />
                  Message Sent!
                </>
              )}
            </button>
          </form>
        </div>

        {/* Contact Information */}
        <div className="space-y-8">
          {/* Map Card */}
          <div className="bg-[#679729]/5 rounded-2xl p-8 transition-transform hover:scale-[1.02]">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#679729]/10 rounded-lg">
                <MapPin className="w-6 h-6 text-[#679729]" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Visit Us At</h3>
                <p className="text-gray-600">
                  I-504, KP Residency, Avkash Park Society 
                  <br />
                  Nikol, Ahmedabad, Gujarat 382350
                </p>
              </div>
            </div>
          </div>

          {/* Phone Card */}
          <div className="bg-[#679729]/5 rounded-2xl p-8 transition-transform hover:scale-[1.02]">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#679729]/10 rounded-lg">
                <Phone className="w-6 h-6 text-[#679729]" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Call Us</h3>
                <p className="text-gray-600">
                  Mon-Fri from 9am to 6pm
                  <br />
                  <a href="tel:+917203802493" className="text-[#679729] hover:underline">
                    +91 720 3802 493
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Email Card */}
          <div className="bg-[#679729]/5 rounded-2xl p-8 transition-transform hover:scale-[1.02]">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#679729]/10 rounded-lg">
                <Mail className="w-6 h-6 text-[#679729]" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Email Us</h3>
                <p className="text-gray-600">
                  We'll respond within 24 hours
                  <br />
                  <a href="mailto:organicpads.shop@gmail.com" className="text-[#679729] hover:underline">
                    organicpads.shop@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Hours Card */}
          <div className="bg-[#679729]/5 rounded-2xl p-8 transition-transform hover:scale-[1.02]">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#679729]/10 rounded-lg">
                <Clock className="w-6 h-6 text-[#679729]" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-2">Business Hours</h3>
                <div className="text-gray-600 space-y-1">
                  <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                  <p>Saturday: 9:00 AM - 1:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify(schemaMarkup)}
      </script>
    </>
  );
};

export default ContactUs;